/**
 * Shared time formatting utilities
 */

/**
 * Format a date as "Apr 10, 2026" (no time component)
 */
export function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a relative time string (e.g., "2h ago", "in 3h 45m")
 */
export function formatRelativeTime(
  dateStr: string,
  direction: "past" | "future"
): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs =
    direction === "past"
      ? now.getTime() - date.getTime()
      : date.getTime() - now.getTime();

  if (diffMs < 0) {
    return direction === "past" ? "just now" : "now";
  }

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours === 0) {
    if (minutes === 0) return direction === "past" ? "just now" : "now";
    return direction === "past" ? `${minutes}m ago` : `in ${minutes}m`;
  }

  if (hours < 24) {
    const timeStr =
      remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    return direction === "past" ? `${timeStr} ago` : `in ${timeStr}`;
  }

  const days = Math.floor(hours / 24);
  return direction === "past" ? `${days}d ago` : `in ${days}d`;
}

/**
 * Format an absolute time string (e.g., "Jan 15, 2:30 PM")
 */
export function formatAbsoluteTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format a time with today/tomorrow context
 * Returns "Today at 2:30 PM", "Tomorrow at 9:00 AM", or "Jan 15, 2:30 PM"
 */
export function formatNextTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  const time = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (isToday) return `Today at ${time}`;
  if (isTomorrow) return `Tomorrow at ${time}`;

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
